import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Popconfirm, Space, Spin, Table, Tag, Tooltip, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { campaignsApi } from '../services/catalogServiceAPI/campaignsProxy';
import { ACCOUNT_OPTIONS, DATE_FORMAT, ENGAGEMENT_METRICS, PLACEMENT_LABELS, PLATFORM_OPTIONS, SALE_KIND_LABELS } from './constants';

const { Title, Paragraph, Text } = Typography;

const fmt = (iso) => (iso ? dayjs(iso).format(DATE_FORMAT) : null);

function statusTag(row) {
    switch (row.status) {
        case 'live': {
            const now = Date.now();
            if (row.startAt && new Date(row.startAt).getTime() > now) return <Tag color="blue">Scheduled</Tag>;
            if (row.endAt && new Date(row.endAt).getTime() <= now) return <Tag color="volcano">Ended</Tag>;
            return <Tag color="green">Live</Tag>;
        }
        case 'paused': return <Tag color="gold">Paused</Tag>;
        case 'archived': return <Tag>Archived</Tag>;
        default: return <Tag color="default">Draft</Tag>;
    }
}

const labelsOf = (values, options) => (values ?? []).map((v) => options.find((o) => o.value === v)?.label ?? v);
const hasRange = (range) => range && (range.min != null || range.max != null);

/** A short line: the headline rules, then "+N rules" for the rest. */
function audienceSummary(audience) {
    const a = audience ?? {};
    const parts = [];
    if (a.platforms?.length) parts.push(labelsOf(a.platforms, PLATFORM_OPTIONS).join(', '));
    if (a.accounts?.length) parts.push(labelsOf(a.accounts, ACCOUNT_OPTIONS).join(', '));
    if (a.plans?.length) parts.push(a.plans.join(', '));
    if (typeof a.emailVerified === 'boolean') parts.push(a.emailVerified ? 'Email verified' : 'Email not verified');
    let more = 0;
    if (a.minAppVersion || a.maxAppVersion) more += 1;
    if (a.subscriptionStates?.length) more += 1;
    if (a.providers?.length) more += 1;
    if (a.countries?.length) more += 1;
    if (a.languages?.length) more += 1;
    more += ENGAGEMENT_METRICS.filter((m) => hasRange(a.engagement?.[m.key])).length;
    if (a.seenCampaignIds?.length) more += 1;
    if (a.notSeenCampaignIds?.length) more += 1;
    if (a.rolloutPercent != null && a.rolloutPercent < 100) more += 1;
    if (parts.length === 0 && more === 0) return <Text type="secondary">Everyone</Text>;
    return (
        <span>
            {parts.join(' · ')}
            {more > 0 && <Text type="secondary">{parts.length > 0 ? ' ' : ''}+{more} rule{more === 1 ? '' : 's'}</Text>}
        </span>
    );
}

/**
 * Admin-authored in-app dialogs shown at fixed moments to the users who match
 * their audience rules, on web and in the apps, with no release.
 */
export function Campaigns() {
    const history = useHistory();
    const [rows, setRows] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        try {
            setRows(await campaignsApi.list());
        } catch (e) {
            message.error(`Failed to load campaigns: ${e.message}`);
            setRows([]);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggle = async (row) => {
        const { id, stats, createdBy, createdAt, updatedAt, ...body } = row;
        setBusyId(id);
        try {
            await campaignsApi.update(id, { ...body, status: row.status === 'live' ? 'paused' : 'live' });
            await load();
        } catch (e) {
            message.error(`Failed to update: ${e.message}`);
        } finally {
            setBusyId(null);
        }
    };

    const remove = async (id) => {
        try {
            await campaignsApi.remove(id);
            load();
        } catch (e) {
            message.error(`Failed to delete: ${e.message}`);
        }
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            render: (name, row) => (
                <Space size={6}>
                    <Link to={`/campaigns/edit/${row.id}`}>{name}</Link>
                    {row.sale?.kind && <Tag color="gold">{SALE_KIND_LABELS[row.sale.kind] ?? 'Sale'}</Tag>}
                </Space>
            ),
        },
        { title: 'Status', width: 110, render: (_, row) => statusTag(row) },
        {
            title: 'Placements',
            render: (_, row) => (
                <Space size={[4, 4]} wrap>
                    {(row.placements ?? []).map((p) => <Tag key={p}>{PLACEMENT_LABELS[p] ?? p}</Tag>)}
                </Space>
            ),
        },
        { title: 'Audience', render: (_, row) => audienceSummary(row.audience) },
        {
            title: 'Window',
            width: 170,
            render: (_, row) => (row.startAt || row.endAt
                ? <Text style={{ fontSize: 12 }}>{fmt(row.startAt) ?? 'now'} → {fmt(row.endAt) ?? 'open'}</Text>
                : <Text type="secondary">Always</Text>),
        },
        { title: 'Priority', dataIndex: 'priority', width: 80, align: 'right' },
        {
            title: 'Stats',
            width: 190,
            render: (_, row) => {
                const s = row.stats ?? {};
                return (
                    <Tooltip title={`${s.shown ?? 0} shown · ${s.cta ?? 0} pressed a button · ${s.dismissed ?? 0} dismissed · ${s.reached ?? 0} distinct users or devices`}>
                        <Text style={{ fontSize: 12 }}>
                            {s.shown ?? 0} / {s.cta ?? 0} / {s.dismissed ?? 0}
                            <Text type="secondary"> · {s.reached ?? 0} reached</Text>
                        </Text>
                    </Tooltip>
                );
            },
        },
        { title: 'Updated', dataIndex: 'updatedAt', width: 140, render: (v) => <Text style={{ fontSize: 12 }}>{fmt(v)}</Text> },
        {
            title: '',
            width: 300,
            render: (_, row) => (
                <Space size={4} wrap>
                    <Button size="small" onClick={() => history.push(`/campaigns/edit/${row.id}`)}>Edit</Button>
                    <Button size="small" onClick={() => history.push('/campaigns/new', { duplicateOf: row })}>Duplicate</Button>
                    {row.status !== 'archived' && (
                        <Button size="small" loading={busyId === row.id} onClick={() => toggle(row)}>
                            {row.status === 'live' ? 'Pause' : 'Go live'}
                        </Button>
                    )}
                    <Popconfirm
                        title="Delete this campaign?"
                        description={row.stats?.shown > 0 ? `It was shown ${row.stats.shown} times; the stats go with it.` : undefined}
                        onConfirm={() => remove(row.id)}
                    >
                        <Button size="small" danger>Delete</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Space style={{ marginBottom: 8 }} align="center">
                <Title level={3} style={{ margin: 0 }}>Campaigns</Title>
                <Button type="primary" onClick={() => history.push('/campaigns/new')}>New campaign</Button>
            </Space>
            <Paragraph type="secondary">
                In-app dialogs (a sale, an event, &ldquo;rate us&rdquo;) shown at a chosen moment to the users who match the audience rules, on web and in the apps, with no release.
                Stats read shown / pressed a button / dismissed.
            </Paragraph>
            {rows === null ? <Spin /> : (
                <Table rowKey="id" dataSource={rows} columns={columns} pagination={false} locale={{ emptyText: 'No campaigns yet' }} />
            )}
        </div>
    );
}
